<script setup>
import { computed } from 'vue'

import { getI18n } from '@/plugins/i18n'
import { formatShortDate } from '@/lib/utils'

import { useAccountingStore } from '@/stores/accounting.store'
import { usePlanningStore } from '@/stores/planning.store'
import { useWebSiteStore } from '@/stores/web-site.store'

import { expenseRowAmount, formatExpenseMoney } from '@/composables/expense-claim'

import ListItem from '@/components/shared/ListItem.vue'
import ExpenseItemAvatar from '@/components/accounting/ExpenseItemAvatar.vue'

const props = defineProps({
  row: {
    type: Object,
    required: true,
  },
  showDate: {
    type: Boolean,
    default: false,
  },
  showProject: {
    type: Boolean,
    default: true,
  },
  showStaff: {
    type: Boolean,
    default: false,
  },
  selected: {
    type: Boolean,
    default: false,
  },
  avatarSelectable: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['avatar-click'])

const onAvatarClick = (event) => {
  if (!props.avatarSelectable) return
  event.stopPropagation()
  emit('avatar-click')
}

const i18n = getI18n()
const accountingStore = useAccountingStore()
const webSiteStore = useWebSiteStore()
const planningStore = usePlanningStore()
const staffName = computed(() => (props.showStaff ? planningStore.staff.find(staff => staff.id === props.row.staff)?.surname_name : null))
const tagId = computed(() => props.row.tag_id ?? props.row.tag)
const tag = computed(() => accountingStore.costReportRowTagById.get(tagId.value))
const tagName = computed(() => tag.value?.name ?? i18n.t('common.expense'))
const amount = computed(() => props.row.amount ?? expenseRowAmount(props.row))
const caption = computed(() => [
  props.showDate ? formatShortDate(props.row.date) : null,
  props.showProject ? props.row.project_name ?? (props.row.project ? null : i18n.t('common.generalExpenses')) : null,
].filter(Boolean).join(' · '))
const fileCount = computed(() => props.row.row_files?.length ?? 0)
const currencyRef = computed(() => props.row.currency_ref ?? webSiteStore.getDefaultCurrency()?.id)
</script>

<template>
  <list-item
    class="q-pa-sm"
    dense
    :label="tagName"
  >
    <template #avatar>
      <slot name="avatar">
        <div class="avatar-flip" :class="{ 'avatar-flip--flipped': props.selected }" @click="onAvatarClick">
          <expense-item-avatar class="avatar-flip__face" :item="tag" />
          <q-avatar
            class="avatar-flip__face avatar-flip__face--back"
            size="md"
            color="primary"
            text-color="white"
            icon="mdi-check"
          />
        </div>
      </slot>
    </template>
    <template #caption>
      <div v-if="staffName">{{ staffName }}</div>
      <div v-if="caption">{{ caption }}</div>
    </template>
    <template #right>
      <q-item-section side class="text-right">
        <q-item-label class="text-weight-medium">{{ formatExpenseMoney(amount, currencyRef) }}</q-item-label>
        <q-item-label caption>
          <q-icon :name="fileCount ? 'mdi-paperclip' : 'mdi-paperclip-off'" :color="fileCount ? 'grey-7' : 'grey-5'" />
          <span v-if="fileCount">{{ fileCount }}</span>
        </q-item-label>
        <q-item-label v-if="props.row.expense_claim_number" caption>
          <q-icon name="mdi-wallet-outline" size="xs" /> {{ props.row.expense_claim_number }}
        </q-item-label>
      </q-item-section>
      <slot name="actions" />
    </template>
  </list-item>
</template>

<style scoped lang="scss">
.avatar-flip {
  position: relative;
  transform-style: preserve-3d;
  transition: transform .3s ease;
}

.avatar-flip--flipped {
  transform: rotateY(180deg);
}

.avatar-flip__face {
  backface-visibility: hidden;
}

.avatar-flip__face--back {
  position: absolute;
  inset: 0;
  transform: rotateY(180deg);
}
</style>
